<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from 'vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  buttonTitle: {
    type: String,
    default: null,
  },
  onButtonActivate: {
    type: Function,
    default: () => {},
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  visible: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: null,
  },
  maxWidth: {
    type: [String, Number],
    default: '500',
  },
  small: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    required: true,
  },
  profiles: {
    type: Array,
    default: () => [],
  },
  hierarchy: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['input', 'update:filters'])

const baseId = ref(`hazard-table-settings-${Math.random().toString(36).slice(2, 8)}`)
const dialog = ref(false)
const localFilters = reactive({
  profile: null,
  sourcesFilter: [],
  testsFilter: [],
  customProfile: [],
  hideND: true,
  chemicalStr: null,
})

const proxy = getCurrentInstance().proxy

const getHazardSourceTypes = computed(() => proxy.$store.getters.getHazardSourceTypes)
const getHazardTestTypes = computed(() => proxy.$store.getters.getHazardTestTypes)
const activatorButtonId = computed(() => `${baseId.value}-button`)
const profileSelectId = computed(() => `${baseId.value}-profile`)

const customProfileDisabled = computed(() => localFilters.profile !== 'custom')

const filtersChanged = computed(() => {
  const sourceCount = getHazardSourceTypes.value.reduce((count, source) => count + source.children.length, 0)
  const testCount = getHazardTestTypes.value.reduce((count, test) => count + test.children.length, 0)

  return localFilters.sourcesFilter.length !== sourceCount || localFilters.testsFilter.length !== testCount
})

const onReset = () => {
  localFilters.sourcesFilter = getHazardSourceTypes.value
    .reduce((result, source) => {
      result.push(...source.children)
      return result
    }, [])
    .map((source) => source.id)

  localFilters.testsFilter = getHazardTestTypes.value
    .reduce((result, test) => {
      result.push(...test.children)
      return result
    }, [])
    .map((test) => test.id)
}

watch(
  () => props.value,
  (value) => {
    dialog.value = value
  },
)

watch(dialog, (value) => {
  emit('input', value)
})

watch(
  localFilters,
  (value) => {
    emit('update:filters', value)
  },
  { deep: true },
)

onMounted(() => {
  Object.assign(localFilters, props.filters)
})
</script>

<template>
  <v-menu
    v-model="dialog"
    offset-y
    :close-on-content-click="false"
    :max-width="maxWidth"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        :id="activatorButtonId"
        v-if="buttonTitle"
        class="border flat-button"
        v-bind="attrs"
        v-on="on"
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        :aria-label="title"
        @click="onButtonActivate"
      >
        <template v-if="buttonTitle.startsWith('mdi-')">
          <v-badge
            v-if="filtersChanged"
            dot
            color="primary"
            offset-x="-4"
            offset-y="0"
          >
            <v-icon>
              {{ buttonTitle }}
            </v-icon>
          </v-badge>

          <v-icon v-else>
            {{ buttonTitle }}
          </v-icon>
        </template>

        <template v-else>
          {{ title }}
        </template>
      </v-btn>

      <v-tooltip
        v-if="title"
        top
        v-bind="defaultVTooltipProps"
        :activator="`#${activatorButtonId}`"
      >
        {{ title }}
      </v-tooltip>
    </template>
    <v-card class="settings-menu-card">
      <v-card-title>{{ title }}</v-card-title>

      <v-card-text class="settings-menu-card__content">
        <div class="d-flex flex-column gap-4">
          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Hazard assessment profile</div>
            <b-form-select
              :id="profileSelectId"
              v-model="localFilters.profile"
              :options="profiles"
              style="max-width: fit-content"
            />

            <v-tooltip
              top
              v-bind="defaultVTooltipProps"
              :activator="`#${profileSelectId}`"
            >
              Hazard assessment profile
            </v-tooltip>
          </div>
          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Categories and Endpoints for Custom profile</div>
            <v-treeview
              v-model="localFilters.customProfile"
              :disabled="customProfileDisabled"
              selectable
              :items="hierarchy"
              dense
            />
          </div>
          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Data sources filter</div>
            <v-treeview
              v-model="localFilters.sourcesFilter"
              selectable
              :items="getHazardSourceTypes"
              dense
            />
          </div>
          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Test types filter</div>
            <v-treeview
              v-model="localFilters.testsFilter"
              selectable
              :items="getHazardTestTypes"
              dense
              item-key="id"
              item-text="id"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="onReset"
          aria-label="Reset"
        >
          Reset
        </v-btn>
        <v-btn
          @click="dialog = false"
          aria-label="Close"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style scoped>
/* .settings-menu-card {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-menu-card__content {
  overflow-y: auto;
} */
</style>
